/**
 * ValidationResultsDialog Component
 *
 * Modal dialog showing detailed validation results comparing expected vs actual items.
 *
 * Features:
 * - Three-column table: Expected | Difference | Actual
 * - Color coding for discrepancies:
 *   - GREEN: Match (expected === actual)
 *   - ORANGE: Mismatch (different quantities)
 *   - RED: Missing (expected but not detected)
 *   - BLUE: Unexpected (detected but not expected)
 * - Each class gets its own row with counts
 */

import React, { useEffect } from 'react';
import { X, CheckCircle, AlertCircle, AlertTriangle, Plus } from 'lucide-react';
import { SectionValidationResult } from '@/types';
import { useCategoryContext } from '@/contexts/CategoryContext';
import { getClassColor } from '@/utils/colorUtils';
import ThumbnailPreview from '@/components/ThumbnailPreview';
import { formatClassNameForDisplay } from '@/utils/classNameUtils';

interface ValidationResultsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  validationResult: SectionValidationResult | null;
}

export const ValidationResultsDialog: React.FC<ValidationResultsDialogProps> = ({
  isOpen,
  onClose,
  validationResult,
}) => {
  const { getClassCategory, getClassThumbnail } = useCategoryContext();

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  if (!isOpen || !validationResult) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Validation Results
            </h2>
            <div className="text-sm text-gray-600 mt-1">
              {validationResult.section_name}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Status Summary */}
        <div className="p-6 bg-gray-50 border-b border-gray-200">
          {validationResult.has_discrepancies ? (
            <div className="flex items-center gap-2 text-orange-700">
              <AlertTriangle className="h-5 w-5" />
              <span className="font-medium">Validation issues detected</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-green-700">
              <CheckCircle className="h-5 w-5" />
              <span className="font-medium">All expected items match</span>
            </div>
          )}
        </div>

        {/* Validation Table */}
        <div className="p-6 bg-white">
          <table className="w-full border-collapse bg-white">
            <thead>
              <tr className="border-b-2 border-gray-300">
                <th className="text-left py-3 px-4 font-semibold text-gray-700">
                  Class Name
                </th>
                <th className="text-center py-3 px-4 font-semibold text-gray-700">
                  Expected
                </th>
                <th className="text-center py-3 px-4 font-semibold text-gray-700">
                  Difference
                </th>
                <th className="text-center py-3 px-4 font-semibold text-gray-700">
                  Actual
                </th>
                <th className="text-center py-3 px-4 font-semibold text-gray-700">
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {validationResult.validation_items.map((item) => {
                const difference = item.detected - item.expected;
                const category = getClassCategory(item.class_name);
                const color = getClassColor(item.class_name, category);

                // Determine row color based on status
                let rowClass = '';
                let statusIcon = null;
                let statusText = '';
                let statusColor = '';

                switch (item.status) {
                  case 'match':
                    rowClass = 'bg-green-100/80';
                    statusIcon = <CheckCircle className="h-4 w-4" />;
                    statusText = 'Match';
                    statusColor = 'text-green-800';
                    break;
                  case 'mismatch':
                    rowClass = 'bg-orange-100/80';
                    statusIcon = <AlertTriangle className="h-4 w-4" />;
                    statusText = 'Mismatch';
                    statusColor = 'text-orange-800';
                    break;
                  case 'missing':
                    rowClass = 'bg-red-100/80';
                    statusIcon = <AlertCircle className="h-4 w-4" />;
                    statusText = 'Missing';
                    statusColor = 'text-red-800';
                    break;
                  case 'unexpected':
                    rowClass = 'bg-blue-100/80';
                    statusIcon = <Plus className="h-4 w-4" />;
                    statusText = 'Unexpected';
                    statusColor = 'text-blue-800';
                    break;
                }

                return (
                  <tr
                    key={item.class_name}
                    className={`border-b border-gray-200 ${rowClass}`}
                  >
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full flex-shrink-0"
                          style={{ backgroundColor: color }}
                        />
                        <ThumbnailPreview
                          className={item.class_name}
                          thumbnailUrl={getClassThumbnail(item.class_name)?.url}
                        >
                          <span className="font-medium" style={{ color }}>
                            {formatClassNameForDisplay(item.class_name)}
                          </span>
                        </ThumbnailPreview>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-center text-gray-700 font-mono">
                      {item.expected}
                    </td>
                    <td className={`py-3 px-4 text-center font-mono font-semibold ${
                      difference === 0 ? 'text-green-600' :
                      difference > 0 ? 'text-blue-600' :
                      'text-red-600'
                    }`}>
                      {difference > 0 ? `+${difference}` : difference}
                    </td>
                    <td className="py-3 px-4 text-center text-gray-700 font-mono">
                      {item.detected}
                    </td>
                    <td className="py-3 px-4">
                      <div className={`flex items-center justify-center gap-1 ${statusColor}`}>
                        {statusIcon}
                        <span className="text-sm font-medium">{statusText}</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {validationResult.validation_items.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <AlertCircle className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>No items to validate</p>
              <p className="text-sm mt-1">
                This section has no expected items defined
              </p>
            </div>
          )}
        </div>

        {/* Legend */}
        <div className="p-6 bg-gray-50 border-t border-gray-200">
          <div className="text-xs font-semibold text-gray-700 uppercase mb-3">
            Status Legend
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-green-200 rounded"></div>
              <span className="text-sm text-gray-700">
                <span className="font-medium">Match:</span> Expected quantity equals actual
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-orange-200 rounded"></div>
              <span className="text-sm text-gray-700">
                <span className="font-medium">Mismatch:</span> Different quantities
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-red-200 rounded"></div>
              <span className="text-sm text-gray-700">
                <span className="font-medium">Missing:</span> Expected but not detected
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-blue-200 rounded"></div>
              <span className="text-sm text-gray-700">
                <span className="font-medium">Unexpected:</span> Detected but not expected
              </span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ValidationResultsDialog;
